import { FC, useCallback } from 'react'
import Link from 'next/link'
import { Steps } from 'components/shared'

import PhoneNumberVerificationForm from 'components/forms/phone-number-verification'
import SignUpForm from 'components/forms/sign-up'
import { useQueryParams } from 'hooks'
import CreditMessage from 'components/credit-message'
import DateOfBirthForm from 'components/forms/date-of-birth'
import { ACCOUNT_SETTINGS, GRANTED_REWARDS } from 'routes/paths'
import FollowStarsContainer from '../follow-stars-step'

import styles from '../AuthForm.module.scss'

type SignUpProps = {
  isReferralMode?: boolean
}

const SignUp: FC<SignUpProps> = ({ isReferralMode }) => {
  const {
    paths,
    push,
    query: { redirect, auction, bid, r },
  } = useQueryParams()

  const handleRedirect = useCallback(() => {
    if (auction && bid) {
      push({ pathname: paths.CONFIRM_BID, query: { auction, bid } })
      return
    }

    push(redirect || paths.GRANTED_REWARDS)
  }, [auction, bid, paths.CONFIRM_BID, paths.HOME, push, redirect])

  if (!r && !isReferralMode) {
    return (
      <div className={styles['form-wrapper']}>
        <SignUpForm onCompleted={() => push(ACCOUNT_SETTINGS)} />
      </div>
    )
  }

  return (
    <Steps>
      <Steps.Step>
        {({ toNextStep }) => (
          <div className={styles['form-wrapper']}>
            <SignUpForm
              isReferralMode={isReferralMode}
              onCompleted={() => {
                toNextStep()
              }}
            />
          </div>
        )}
      </Steps.Step>

      <Steps.Step>
        {({ toNextStep }) => (
          <div className={styles['form-wrapper']}>
            <div className="flex flex-col">
              <PhoneNumberVerificationForm
                onCompleted={() => {
                  toNextStep()
                }}
                sendVerificationCodeHeader={
                  <p className="text-center text-lg">
                    Account security is important for us. Verify your mobile number to redeem your
                    credit and create your account!
                  </p>
                }
                checkVerificationCodeHeader={
                  <p className="text-center text-lg">
                    Last step! Enter the code we texted you and you&apos;re in!
                  </p>
                }
                withDisclaimer
              />

              <button
                className="btn btn-text-primary self-center text-g-light-gray btn-sm mt-4"
                onClick={() => push(ACCOUNT_SETTINGS)}
                type="button"
              >
                Skip
              </button>
            </div>
          </div>
        )}
      </Steps.Step>

      <Steps.Step>
        {({ toNextStep }) => (
          <div className="space-y-10 flex flex-col">
            <div className="mx-auto">
              <CreditMessage />
            </div>

            <FollowStarsContainer onCompleted={toNextStep} />

            <button
              onClick={toNextStep}
              className="btn btn-text-primary self-center text-g-light-gray btn-sm"
              type="button"
            >
              Skip and proceed
            </button>
          </div>
        )}
      </Steps.Step>

      <Steps.Step>
        <div className={styles['form-wrapper']}>
          <div className="space-y-6 flex flex-col">
            <CreditMessage />

            <p className="text-center text-lg">
              Earn an additional $2 in credit for adding your birthday.
            </p>

            <DateOfBirthForm onCompleted={handleRedirect} />

            <Link legacyBehavior href={GRANTED_REWARDS}>
              <a
                className="btn btn-text-primary self-center text-g-light-gray btn-sm"
                type="button"
              >
                Skip and proceed to profile
              </a>
            </Link>
          </div>
        </div>
      </Steps.Step>
    </Steps>
  )
}

export default SignUp
